import {
  WORKSPACE_NAME_MAX_LENGTH,
  WORKSPACE_NAME_MIN_LENGTH,
  getErrorMessage,
  isWorkspaceLoginIdValid,
} from '@spotify/confidence-core-react';

import { Region } from './hooks/useCreateAccount';
import type {
  AuthConnection,
  CreateAccountRequest,
} from './hooks/useCreateAccount';

export type AccountDetailsState = {
  displayName: string;
  adminEmail?: string;
  region: Region;
  loginDomain?: string;
  authConnections: Record<AuthConnection, boolean>;
};

export type LoginIdAvailability = {
  available: boolean;
  message?: string;
  loginId: string;
};

type LoginIdAvailabilityErrorOptions = {
  failOpen: boolean;
};

export class LoginIdAvailabilityError extends Error {
  readonly loginId: string;
  readonly failOpen: boolean;

  constructor(
    error: unknown,
    loginId: string,
    { failOpen }: LoginIdAvailabilityErrorOptions,
  ) {
    super(error instanceof Error ? error.message : String(error), {
      cause: error,
    });
    this.name = 'LoginIdAvailabilityError';
    this.loginId = loginId;
    this.failOpen = failOpen;
  }
}

type LoginIdAvailabilityState = {
  loading: boolean;
  value?: LoginIdAvailability;
  error?: Error;
};

type LoginIdAvailabilityStatus = {
  available: boolean;
  message?: string;
  confirmedAvailable: boolean;
};

export const deriveLoginIdAvailability = (
  availabilityState: LoginIdAvailabilityState,
  loginId: string,
): LoginIdAvailabilityStatus => {
  const currentAvailability =
    availabilityState.value?.loginId === loginId
      ? availabilityState.value
      : undefined;
  const currentError =
    availabilityState.error instanceof LoginIdAvailabilityError &&
    availabilityState.error.loginId === loginId
      ? availabilityState.error
      : undefined;
  const available = currentAvailability?.available ?? true;
  const message =
    currentAvailability?.message ??
    (currentError && !currentError.failOpen
      ? "We couldn't check this workspace name. Please try again."
      : undefined);
  const confirmedAvailable =
    !availabilityState.loading &&
    (!!currentError?.failOpen ||
      (!!currentAvailability && available && !currentAvailability.message));

  return {
    available,
    message,
    confirmedAvailable,
  };
};

type BuildCreateAccountRequestOptions = {
  accountDetails: AccountDetailsState;
  loginId: string;
  inviteId?: string;
  marketingOptIn: boolean;
  signupAttribution: CreateAccountRequest['signupAttribution'];
};

export const NAME_MAX_LENGTH = WORKSPACE_NAME_MAX_LENGTH;
export const NAME_MIN_LENGTH = WORKSPACE_NAME_MIN_LENGTH;

export const DEFAULT_STATE: AccountDetailsState = {
  displayName: '',
  region: Region.RegionEu,
  loginDomain: '',
  authConnections: {
    googleAuthConnection: false,
    passwordAuthConnection: false,
  },
};

export const getGoogleLoginMode = (
  loginDomain?: string,
): 'invite-only' | 'domain' =>
  loginDomain === undefined ? 'invite-only' : 'domain';

export const getEnabledAuthConnections = (
  authConnections: Record<AuthConnection, boolean>,
): AuthConnection[] =>
  (Object.entries(authConnections) as [AuthConnection, boolean][])
    .filter(([, enabled]) => enabled)
    .map(([connection]) => connection);

export const buildCreateAccountRequest = ({
  accountDetails,
  loginId,
  inviteId,
  marketingOptIn,
  signupAttribution,
}: BuildCreateAccountRequestOptions): CreateAccountRequest => ({
  displayName: accountDetails.displayName,
  loginId,
  region: accountDetails.region,
  authConnections: getEnabledAuthConnections(
    accountDetails.authConnections,
  ).map(connection => ({
    [connection]: {},
  })) as CreateAccountRequest['authConnections'],
  adminEmail: accountDetails.adminEmail || undefined,
  allowedLoginEmailDomains:
    inviteId && accountDetails.authConnections.googleAuthConnection
      ? [accountDetails.loginDomain].filter((domain): domain is string =>
          Boolean(domain),
        )
      : undefined,
  marketingOptIn,
  signupAttribution,
});

const displayNameRegex = /^[a-zA-Z][a-zA-Z0-9 -]*[a-zA-Z0-9]$/;

export const isDisplayNameValid = (displayName: string) => {
  return (
    displayNameRegex.test(displayName) &&
    displayName.length >= NAME_MIN_LENGTH &&
    displayName.length <= NAME_MAX_LENGTH
  );
};

export const formatDisplayName = (displayName: string) => {
  return displayName.replace(/[^a-zA-Z0-9 -]/g, '').slice(0, NAME_MAX_LENGTH);
};

export const formatLoginId = (loginId: string = '') => {
  return loginId
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toLowerCase()
    .slice(0, NAME_MAX_LENGTH);
};

export const isLoginIdValid = isWorkspaceLoginIdValid;

const nameLengthMessage = `The name must be between ${NAME_MIN_LENGTH} and ${NAME_MAX_LENGTH} characters.`;

export const getWorkspaceNameHelperText = ({
  displayName,
  loginId,
  isModified,
  isAvailable,
  availabilityError,
}: {
  displayName: string;
  loginId: string;
  isModified: boolean;
  isAvailable: boolean;
  availabilityError?: string;
}): string => {
  if (isModified && !/^[a-zA-Z]/.test(displayName)) {
    return 'The name should start with a letter.';
  }
  if (isModified && (displayName.endsWith('-') || displayName.endsWith(' '))) {
    return 'The name cannot end with "-" or a space.';
  }
  if (
    isModified &&
    !isDisplayNameValid(displayName) &&
    displayName.length > 0
  ) {
    return nameLengthMessage;
  }
  if (!isAvailable) {
    return 'This workspace name is already in use.';
  }
  if (availabilityError) {
    return availabilityError;
  }
  if (loginId) {
    return `Your workspace ID will be "${loginId}". You'll need this to sign in.`;
  }
  return 'Shown across Confidence and to your teammates.';
};

const getAuthErrorCode = (error: unknown): string | undefined => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    typeof error.error === 'string'
  ) {
    return error.error;
  }
  return undefined;
};

export const isEmailNotVerifiedError = (error: unknown): boolean => {
  return (
    getAuthErrorCode(error) === 'email_not_verified' ||
    /email (?:is )?not verified|verify your email/i.test(
      getErrorMessage(error, ''),
    )
  );
};

export const isAccountUnderReviewError = (error?: Error) => {
  return Boolean(error && /review/i.test(error.message));
};

export const isAuthCancellationError = (error: unknown): boolean =>
  ['cancelled', 'popup_cancelled', 'popup_closed_by_user'].includes(
    getAuthErrorCode(error) ?? '',
  );

export const getSignupErrorMessage = (error: Error): string => {
  if (/sign-in session expired/i.test(error.message)) {
    return error.message;
  }
  const match = error.message.match(/^\w+:\s*(.+)/);
  return match?.[1] ?? error.message;
};

export const getSignupAuthErrorMessage = (error: unknown): string => {
  const errorCode = getAuthErrorCode(error);
  const errorMessage = getErrorMessage(error, '');

  if (['popup_open', 'popup_blocked'].includes(errorCode ?? '')) {
    return 'Your browser blocked the sign-up window. Allow pop-ups and try again.';
  }
  if (['timeout', 'popup_timeout'].includes(errorCode ?? '')) {
    return 'The sign-up window timed out. Try again.';
  }
  if (
    ['user_exists', 'username_exists'].includes(errorCode ?? '') ||
    /user already exists/i.test(errorMessage)
  ) {
    return 'An account already exists for this email. Log in instead.';
  }
  return "We couldn't complete sign-up. Try again or contact support.";
};

export const getLoginAuthErrorMessage = (error: unknown): string => {
  const errorCode = getAuthErrorCode(error);
  if (['timeout', 'popup_timeout'].includes(errorCode ?? '')) {
    return 'Login timed out. Try again.';
  }
  return "We couldn't log you in. Try again or contact support.";
};

export const getSignupReturnPath = ({
  returnTo,
  origin,
}: {
  returnTo: string | null;
  origin: string;
}): string | undefined => {
  if (!returnTo) return undefined;
  try {
    const returnUrl = new URL(returnTo, origin);

    const isSignupRoute =
      returnUrl.origin === origin &&
      (returnUrl.pathname === '/signup' ||
        returnUrl.pathname.startsWith('/signup/'));
    return isSignupRoute
      ? `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`
      : undefined;
  } catch {
    return undefined;
  }
};
